"use client";

import type { MarketsBoardComponent } from "../../types";
import { resolveLinkHref } from "../../utils/links";
import { ExchangeCard, InflationCard, InterestCard } from "./MarketsCards";
import { MarketsSparkline } from "./MarketsSparkline";

/**
 * Markets board: three "key markets" cards for the home page.
 *
 * Designed to read as a calm, scannable strip rather than a dashboard. The
 * earlier version felt overloaded — colored left edges, ringed cards on a
 * tinted background, two-column currency lists, sparklines on every card —
 * so this revision strips the chrome and lets the data carry the visual
 * weight: bare cards, single-column lists, one sparkline only on the
 * featured exchange pair.
 *
 * The three sub-cards remain optional in the payload; authors can render any
 * subset. On mobile they stack one per row; from `lg` they sit 6/3/3.
 */
export function MarketsBoardRenderer({ component }: { component: MarketsBoardComponent }) {
	const { title, subtitle, actionLabel, actionHref, exchange, inflation, interest } = component.content;
	const resolvedAction = resolveLinkHref(actionHref);

	return (
		<section className="mx-[calc(50%-50vw)] bg-[#d0ebf7] pt-10 pb-14">
			<div className="relative mx-auto max-w-7xl px-5 md:px-8">
				{title ? (
					<header className="mb-6 md:mb-8">
						<h3 className="text-[28px] font-bold tracking-tight text-[#005CA9] md:text-[32px]">
							{title}
						</h3>
						<div className="mt-2 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
						{subtitle ? (
							<p className="mt-2 max-w-2xl text-[16px] xl:text-[18px] leading-relaxed text-slate-600">
								{subtitle}
							</p>
						) : null}
					</header>
				) : null}
				{/* Layout:
				    - mobile  : single column, stacked
				    - md (≥768): exchange full-width, then inflation + interest 50/50 below
				    - lg (≥1024): three equal columns (1/3 each)
				    Avoids shrinking the cards prematurely on tablets.
				    Vitrine mono-carte (ex. « Taux en vigueur » de /politique-monetaire,
				    qui ne référence que le bloc interest) : une seule colonne bornée,
				    sinon la carte isolée flotterait sur un tiers d'une bande vide. */}
				<div className={[exchange, inflation, interest].filter(Boolean).length === 1
					? "grid max-w-xl grid-cols-1 gap-5"
					: "grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-6"}>
					{exchange ? (
						<div className="md:col-span-2 lg:col-span-1">
							<ExchangeCard
								data={exchange}
								actionLabel={actionLabel}
								actionHref={resolvedAction}
								Sparkline={MarketsSparkline}
							/>
						</div>
					) : null}
					{inflation ? (
						<div className="md:col-span-1 lg:col-span-1">
							<InflationCard data={inflation} />
						</div>
					) : null}
					{interest ? (
						<div className="md:col-span-1 lg:col-span-1">
							<InterestCard data={interest} />
						</div>
					) : null}
				</div>
			</div>
		</section>
	);
}
